import { EventEmitter, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { baggageRequestPayloadParts } from '@app/my-booking/sub-components/my-booking-service/sub-components/baggage-request/baggage-request-payload.state';
import { flightMealRequestPayloadParts } from '@app/my-booking/sub-components/my-booking-service/sub-components/flight-meal-request/flight-meal-request-payload.state';
import { loungeServiceRequestPayloadParts } from '@app/my-booking/sub-components/my-booking-service/sub-components/lounge-service-request/lounge-service-request-payload.state';
import { languageSelectionModalParts } from '@common/components/language-selection-modal/language-selection-modal.payload';
import { concatenateTravelerNames, isEmptyArray, isStringEmpty } from '@common/helpers';
import {
  ERROR_MESSAGE_ID,
  FUCNTION_INELIGIBLE_REASON,
} from '@common/helpers/constants/my-booking/fucntion_ineligible_reason.contant';
import {
  DATA_CODE,
  LANGUAGE_EN,
  LANGUAGE_ITALIAN,
  LANGUAGE_JP,
  LANGUAGE_SPANISH,
  POS_COUNTRY_CODE_IT,
  POS_COUNTRY_CODE_MX,
} from '@common/helpers/constants/my-booking/my_booking_service.contant';
import { FEATURE_TO_ROUTE_MAPPING } from '@common/helpers/constants/servicing.contant';
import { MListData, ServicingCommonModel } from '@common/interfaces';
import { ErrorMessages } from '@common/interfaces/master/m_function_ineligble_reason';
import { MSpecialMeal } from '@common/interfaces/master/m_special_meal';
import {
  INITIAL_MYBOOKING_SERVICE_MASTER_DATA,
  INITIAL_MYBOOKING_SERVICE_STATE,
  INITIAL_SHOW_HIDE_SERVICE_STATE,
  MasterDataS01P033,
  MybookingServiceState,
  ShowHideServiceState,
} from '@common/interfaces/mybooking/mybooking-service.interface';
import { FirstBaggageBoundMap, FirstBaggagePassengerApplyInfo } from '@common/interfaces/mybooking/mybooking.interface';
import {
  CallCenterRequestBaggage,
  CenterRequestBaggagePassengerInfor,
  FirstBaggagePassengerMap,
  FirstBaggagePnr,
} from '@common/interfaces/mybooking/order-baggage.interface';
import {
  PassengerApplicationStatusMap,
  SegmentMap,
} from '@common/interfaces/servicing-seatmap/seat-reservation-status';
import { ServicingSeatmapBackupStoreService } from '@common/services/seatmap/servicing-seatmap-backup-store.service';
import { ServicingSeatmapStoreService } from '@common/services/seatmap/servicing-seatmap-store.service';
import { ServicingCommonStoreService } from '@common/services/servicing-common-store/servicing-common-store.service';
import { MasterJsonKeyPrefix } from '@conf/asw-master.config';
import { SERVICING_ROUTES } from '@conf/routes.config';
import { DialogComponent } from '@lib/components/base-ui-components/dialog/dialog.component';
import { SupportClass } from '@lib/components/support-class';
import { DialogClickType, PageType } from '@lib/interfaces';
import { AmountFormatPipe, StaticMsgPipe } from '@lib/pipes';
import {
  AswServiceStoreService,
  CommonLibService,
  DialogDisplayService,
  ErrorsHandlerService,
  ModalService,
  SystemDateService,
} from '@lib/services';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import {
  GetOrderResponseData,
  GetOrderResponseDataOrderEligibilitiesMyCarValet,
  OrderSeatItemSeatSelection,
  Traveler,
  TravelerNamesInner,
} from 'src/sdk-servicing';
import { OrderSeatItem } from 'src/sdk-servicing/model/orderSeatItem';

@Injectable()
export class MyBookingServiceService extends SupportClass {
  transitionSourceFullFunctionPageId: string = '';

  dynamicMsgKeyPrefix: string = MasterJsonKeyPrefix.DYNAMIC;

  myBookingServiceInformation$ = new BehaviorSubject<ServicingCommonModel>({});
  myBookingServiceShowHideState$ = new BehaviorSubject<ShowHideServiceState>(INITIAL_SHOW_HIDE_SERVICE_STATE);
  myBookingServiceMasterData$ = new BehaviorSubject<MasterDataS01P033>(INITIAL_MYBOOKING_SERVICE_MASTER_DATA);
  mybookingServiceState$ = new BehaviorSubject<MybookingServiceState>(INITIAL_MYBOOKING_SERVICE_STATE);

  constructor(
    private _dialogService: DialogDisplayService,
    private readonly _router: Router,
    private _aswServiceStoreService: AswServiceStoreService,
    private _errorsHandlerSvc: ErrorsHandlerService,
    private _modal: ModalService,
    private _common: CommonLibService,
    private _staticMsg: StaticMsgPipe,
    private _amountFormatPipe: AmountFormatPipe,
    private _systemDateSvc: SystemDateService,
    private _servicingCommonStoreService: ServicingCommonStoreService,
    private _translateSvc: TranslateService,
    private _servicingSeatmapStoreService: ServicingSeatmapStoreService,
    private _servicingSeatmapBackupStoreService: ServicingSeatmapBackupStoreService
  ) {
    super();
  }

  // [table 3] 申込済み搭乗者情報
  getSeatItemPnr(pnr?: GetOrderResponseData, flightId?: string, travelerId?: string): OrderSeatItem {
    return pnr?.seats?.[String(flightId)]?.[String(travelerId)] ?? ({} as OrderSeatItem);
  }

  checkHasChildSeat(seat: OrderSeatItem) {
    return (
      seat?.childSeat === true &&
      seat?.seatSelection?.serviceStatus !== OrderSeatItemSeatSelection.ServiceStatusEnum.Cancelled
    );
  }

  // [table 3] 申込操作中搭乗者情報
  getSeatItemStore(flightId?: string, travelerId?: string): PassengerApplicationStatusMap | undefined {
    return (this.myBookingServiceInformation$.value.seatReservationStatus?.[String(flightId)] as SegmentMap)?.[
      String(travelerId)
    ];
  }

  getChildSeatStore(travelerId?: string) {
    return this.myBookingServiceInformation$.value.seatReservationStatus?.childSeatInfoList?.find(
      (seat) => seat?.travelerId === travelerId
    );
  }

  // [table 4] 申込済み搭乗者情報
  getFirstBaggageItemPnr(pnr?: GetOrderResponseData, boundId?: string, travelerId?: string): FirstBaggagePassengerMap {
    const firstBaggage = pnr?.services?.baggage?.firstBaggage as FirstBaggagePnr;
    const boundItem = firstBaggage?.[String(boundId)];
    return boundItem?.[String(travelerId)] as FirstBaggagePassengerMap;
  }

  // [table 4] 申込操作中搭乗者情報
  getTravelerStore(boundId?: string, travelerId?: string): FirstBaggagePassengerApplyInfo {
    const boundInfo = this.myBookingServiceInformation$.value.firstBaggage?.[boundId!] as FirstBaggageBoundMap;
    return boundInfo?.[travelerId!] as FirstBaggagePassengerApplyInfo;
  }

  getSpecialBaggageTravelerInformationFromPnr(
    pnr?: GetOrderResponseData,
    boundId?: string,
    travelerId?: string
  ): CenterRequestBaggagePassengerInfor[] {
    const firstBaggage = pnr?.services?.baggage?.callCenterRequestBaggage as CallCenterRequestBaggage;
    return firstBaggage?.[String(boundId)]?.[String(travelerId)] as CenterRequestBaggagePassengerInfor[];
  }

  /**
   * ダイアログを開く
   * @param messageId ダイアログのメッセージID
   * @returns @see DialogClickType
   */
  openDialogPromise(messageId: string): Promise<DialogClickType> {
    return new Promise((resolve) => {
      this._dialogService
        .openDialog({ message: MasterJsonKeyPrefix.DYNAMIC + messageId })
        .buttonClick$.subscribe((result) => resolve(result.clickType));
    });
  }

  openDialogConfirm(messageId: string): EventEmitter<DialogComponent> {
    return this._dialogService.openDialog({
      message: this.getDynamicMessage(messageId),
    }).buttonClick$;
  }

  /** 共通No1. 利用不可理由に応じたエラーメッセージ表示処理 */
  onHandleCommonNo1(func?: string, aswImpossible?: Array<any>, dxAPIReason?: string, errorMessageId?: string): void {
    const result =
      this.myBookingServiceMasterData$.value?.mFucntionIneligibleReason?.[String(func)]?.[String(aswImpossible?.[0])]?.[
        String(dxAPIReason)
      ] ?? ([] as Array<ErrorMessages>);
    if (result?.length > 0 && result?.[0]?.error_message_id !== '') {
      this._errorsHandlerSvc.setRetryableError(PageType.PAGE, {
        errorMsgId: result?.[0]?.error_message_id,
      });
    } else {
      this._errorsHandlerSvc.setRetryableError(PageType.PAGE, {
        errorMsgId: errorMessageId,
      });
    }
  }

  /** 共通No3. 全サービスの編集情報破棄処理 */
  onHandleCommonNo3() {
    const servicingCommon: ServicingCommonModel = {
      ...this._servicingCommonStoreService.servicingCommon,
      meal: undefined,
      chargeableLounge: undefined,
      firstBaggage: undefined,
      seatReservationStatus: undefined,
    };

    this._servicingCommonStoreService.updateServicingCommon({
      ...servicingCommon,
    });

    this._servicingSeatmapStoreService.setServicingSeatmap({});
    this._servicingSeatmapBackupStoreService.setServicingSeatmapBackup({});
  }

  /** 共通No2. 申込操作中情報有無判定処理 */
  onHandleCommonNo2(): boolean {
    let applicationProgressFlag = false;
    if (
      /** 2.1. 事前座席指定.申込操作中フラグ = true */
      this._servicingCommonStoreService.servicingCommon.seatReservationStatus?.isApplicationProcessed ||
      /** 2.2. ラウンジ情報.申込操作中フラグ = true */
      this._servicingCommonStoreService.servicingCommon.chargeableLounge?.isApplicationProcessed ||
      /** 2.3. 事前追加手荷物.申込操作中フラグ = true */
      this._servicingCommonStoreService.servicingCommon.firstBaggage?.isApplicationProcessed ||
      /** 2.4. 機内食情報.申込操作中フラグ = true */
      this._servicingCommonStoreService.servicingCommon.meal?.isApplicationProcessed
    ) {
      applicationProgressFlag = true;
    }
    /** 3. 申込操作中有無を返却する。 */
    return applicationProgressFlag;
  }

  async onHandleYamatoBaggageStep1(): Promise<boolean> {
    const appOperationFlag = this.onHandleCommonNo2();
    let isCancelFunction = false;
    if (appOperationFlag) {
      await firstValueFrom(this.openDialogConfirm('MSG0527')).then((result) => {
        if (result.clickType === DialogClickType.CONFIRM) {
          /**確認ダイアログより閉じるボタン押下が返却された場合、「当画面共通処理No3. 全サービスの編集情報破棄処理」を実施し、イベント処理No7を終了する。 */
          this.onHandleCommonNo3();
        } else if (result.clickType === DialogClickType.CLOSE) {
          isCancelFunction = true;
        }
      });
    }
    return isCancelFunction;
  }

  onHandleYamatoBaggageStep2(pnr?: GetOrderResponseData): void {
    /**2. 'PNR情報.data.orderEligibilities.yamatoBaggage.isEligible = false (利用不可)、かつPNR情報.data.orderEligibilities.yamatoBaggage.nonEligibilityReasonsが存在する場合、 */
    if (
      !pnr?.orderEligibilities?.yamatoBaggage?.isEligible &&
      !isEmptyArray(pnr?.orderEligibilities?.yamatoBaggage?.nonEligibilityReasons)
    ) {
      /**
       * 以下パラメータで「当画面共通処理No1.利用不可理由に応じたエラーメッセージ表示処理」を実施する
       * 機能: "yamatoBaggage"
       * ASW不可事由: PNR情報取得API応答.data.orderEligibilities.yamatoBaggagenonEligibilityReasons
       * DxAPI不可事由: "-"
       * エラーメッセージID: "E1131"
       */
      this.onHandleCommonNo1(
        FUCNTION_INELIGIBLE_REASON.YAMATO_BAGGAGE,
        pnr?.orderEligibilities?.yamatoBaggage?.nonEligibilityReasons,
        '-',
        ERROR_MESSAGE_ID.YAMATO_BAGGAGE
      );

      /** 3. ヤマト手荷物案内(S06-P020)へ遷移する。*/
    } else {
      /** 以下の値をパラメータとして指定し、ヤマト手荷物案内(S06-P020)へ遷移する。 */
      const navigateParams = {
        orderId: this._common.aswServiceStoreService.aswServiceData?.orderId,
        travelDocumentId: this._common.aswServiceStoreService.aswServiceData?.travelDocumentId,
        firstName: this._common.aswServiceStoreService.aswServiceData?.firstName,
        lastName: this._common.aswServiceStoreService.aswServiceData?.lastName,
      };
      this._aswServiceStoreService.updateAswService(navigateParams);
      this._router.navigate([FEATURE_TO_ROUTE_MAPPING['S06-P020']]);
    }
  }

  /** ラウンジ申込(S02-M015)をモーダル表示する。*/
  openLoungeModal(): void {
    const part = loungeServiceRequestPayloadParts();
    this._modal.showSubPageModal(part);
  }

  /** 機内食申込(S02-M017)をモーダル表示する。*/
  openInFlightMealModal(): void {
    const part = flightMealRequestPayloadParts();
    this._modal.showSubModal(part);
  }

  /** 手荷物申込(S02-M016)をモーダル表示する。*/
  openBaggageApplicationModal(): void {
    const part = baggageRequestPayloadParts();
    this._modal.showSubModal(part);
  }

  /** LanguageSelectionModalComponent */
  openLanguageSelectionModal(): void {
    const part = languageSelectionModalParts({
      /**
       * (18)「選択する」ボタン押下処理 (言語選択モーダル)
       * (18.2) マイカーバレー申込(S02-P011)へ遷移する
       */
      continueEvent: () => {
        this._router.navigateByUrl(SERVICING_ROUTES.myCarValetInput);
      },
    });
    this._modal.showSubModal(part);
  }

  /**
   * 搭乗者姓名 : 当該搭乗者情報.names[0].firstName + "" "" + 当該搭乗者情報.names[0].lastName
   */
  getTravelerName(travelerNames?: Array<TravelerNamesInner>): string {
    return concatenateTravelerNames(travelerNames?.[0]);
  }

  getAccompanyingTraveler(traveler?: Traveler): string {
    const children =
      this.mybookingServiceState$.value.pnr?.travelers?.find(
        (travel) => travel.id === traveler?.accompanyingTravelerId
      ) ?? {};
    return concatenateTravelerNames(children.names?.[0]);
  }

  getAccompanyingTravelerRemoveTitle(traveler?: Traveler): string {
    const children =
      this.mybookingServiceState$.value.pnr?.travelers?.find(
        (travel) => travel.id === traveler?.accompanyingTravelerId
      ) ?? {};
    const names = children.names?.[0];
    return concatenateTravelerNames({
      ...names,
      title: undefined,
    });
  }

  isAccompanyingTraveler(traveler?: Traveler): boolean {
    return Boolean(traveler?.accompanyingTravelerId);
  }

  /** 動的メッセージを取得する */
  getDynamicMessage(messageId: string): string {
    return MasterJsonKeyPrefix.DYNAMIC + messageId;
  }

  // (共通No4) 空港名称取得処理
  onHandleCommonNo4(airportCode?: string): string {
    return this.myBookingServiceMasterData$.value.airportI18nAll?.[String(airportCode)]?.[0].airport_name ?? '';
  }

  // (共通No5) 座席属性名称取得処理
  onHandleCommonNo5(seatAttribute?: string): string {
    /** 汎用マスターデータ（リスト）*/
    /** データコード = "PD_035" AND 言語コード = ユーザ共通.言語情報 AND value = 入力パラメータ.座席属性 */
    const mListData =
      this.myBookingServiceMasterData$.value.listData?.find(
        (item: MListData) =>
          item?.data_code === DATA_CODE.PD_035 &&
          item?.lang === this._common.aswContextStoreService.aswContextData.lang &&
          item?.value === seatAttribute
      ) ?? ({} as MListData);
    return mListData?.display_content ?? '';
  }

  /** (共通No6) コールセンター申込手荷物取得処理 */
  onHandleCommonNo6(ssrCode?: string): string {
    /** 1.コールセンター申込手荷物に""(空欄)を設定する */
    let callCenterAppBaggage: string = '';

    /** 2.以下の条件でASWDBの「汎用マスターデータ（リスト）」テーブルから情報を取得する */
    /**
     * テーブル: 汎用マスターデータ（リスト）
     * 検索条件: データコード = "PD_730" AND 言語コード = ユーザ共通.言語情報 AND value = 入力パラメータ.SSRコード
     */
    const mListData =
      this.myBookingServiceMasterData$.value.listData?.find(
        (item: MListData) =>
          item?.data_code === DATA_CODE.PD_730 &&
          item?.lang === this._common.aswContextStoreService.aswContextData.lang &&
          item?.value === ssrCode
      ) ?? ({} as MListData);

    /** 3.取得できた場合、コールセンター申込手荷物に汎用マスターデータ（リスト）.表示内容を設定する */
    callCenterAppBaggage = mListData?.display_content ?? '';

    /** 4.コールセンター申込手荷物を返却する */
    return callCenterAppBaggage;
  }

  /** (共通No7) 特別機内食名称取得処理 */
  onHandleCommonNo7(ssrCode?: string): string {
    // 3.	以下の条件でASWDBの「特別機内食(国際化)」テーブルから
    const itemInfo: MSpecialMeal =
      this.myBookingServiceMasterData$.value.mSpecialMeal?.find(
        (data) =>
          data.ssr_code === ssrCode &&
          Number(data?.apply_from_date) <= Number(this._systemDateSvc.getFormattedSystemDate('yyyyMMdd')) &&
          Number(data?.apply_to_date) >= Number(this._systemDateSvc.getFormattedSystemDate('yyyyMMdd'))
      ) ?? ({} as MSpecialMeal);

    return itemInfo?.special_meal_name ?? '';
  }

  /** (共通No8) 有料機内食名称取得処理 */
  onHandleCommonNo8(ssrCode?: string): string {
    const _lang = this._common.aswContextStoreService.aswContextData.lang;
    const itemInfo = this.myBookingServiceMasterData$.value.listData?.find(
      (item: MListData) => item?.data_code === DATA_CODE.PD_950 && item?.lang === _lang && item.value === ssrCode
    );
    return itemInfo?.display_content ?? '';
  }

  /** (共通No9) アテンド名称取得処理 */
  onHandleCommonNo9(ssrCode?: string): string {
    /** 1.アテンド名称に""(空欄)を設定する。 */
    let attendantName: string = '';

    /**
     * 2.以下の条件でASWDBの「汎用マスターデータ（リスト）」テーブルから情報を取得する。
     * テーブル: 汎用マスターデータ（リスト）
     * キャッシュキー: 汎用マスターデータ（リスト）の名称リスト
     * 検索条件: データコード = "PD_720" AND 言語コード = ユーザ共通.言語情報 AND value = 入力パラメータ.SSRコード
     */
    const itemInfo = this.myBookingServiceMasterData$.value.listData?.find(
      (item: MListData) =>
        item?.data_code === DATA_CODE.PD_720 &&
        item?.lang === this._common.aswContextStoreService.aswContextData.lang &&
        item.value === ssrCode
    );

    /** 3.取得できた場合、アテンド名称に汎用マスターデータ（リスト）.表示内容を設定する。*/
    attendantName = itemInfo?.display_content ?? '';

    /** 4.'アテンド名称を返却する。*/
    return attendantName;
  }

  /** (共通No10) 配慮系サポート内容取得処理 */
  onHandleCommonNo10(ssrCode?: string): string {
    /** ユーザ共通.言語情報 */
    const aswContextLang = this._common.aswContextStoreService.aswContextData.lang;

    /** 1.配慮系サポート内容に""(空欄)を設定する */
    let considerationSupportContent: string = '';
    const mListData =
      this.myBookingServiceMasterData$.value.listData?.find(
        (item: MListData) =>
          item?.data_code === DATA_CODE.PD_750 && item?.lang === aswContextLang && item?.value === ssrCode
      ) ?? ({} as MListData);
    considerationSupportContent = mListData?.display_content ?? '';
    return considerationSupportContent;
  }

  /** (共通No11) 言語選択モーダル表示メッセージ判定処理 */
  onHandleCommonNo11(): string {
    // 1.	表示メッセージに""(空欄)を設定する。
    let displayMessage: string = '';

    // 2.	モーダル共通処理(G01-011)のパラメータ.payloadに設定するメッセージを、以下の通り設定する。
    // 2.1.	ユーザ共通.POS国コード="MX"(メキシコ)、かつユーザ共通.言語情報="es"(スペイン語)の場合、
    // 表示メッセージにスペイン語で当サービスを希望の際はコールセンターへの問い合わせを促す旨を設定する。
    if (
      this._common.aswContextStoreService.aswContextData.posCountryCode === POS_COUNTRY_CODE_MX &&
      this._common.aswContextStoreService.aswContextData.lang === LANGUAGE_SPANISH
    ) {
      displayMessage = 'label.messageinspainish';
    }
    // 2.2.	ユーザ共通.POS国コード="IT"(イタリア)、かつユーザ共通.言語情報="it"(イタリア語)の場合、
    // 表示メッセージにイタリア語で当サービスを希望の際はコールセンターへの問い合わせする旨を設定する。
    else if (
      this._common.aswContextStoreService.aswContextData.posCountryCode === POS_COUNTRY_CODE_IT &&
      this._common.aswContextStoreService.aswContextData.lang === LANGUAGE_ITALIAN
    ) {
      displayMessage = 'label.messageinitalian';
    } else {
      // 2.3.	上記以外の場合、表示メッセージに"MSG1015"(言語切り替えの旨)を設定する。
      displayMessage = `${MasterJsonKeyPrefix.DYNAMIC}MSG1015`;
    }

    // 3.	表示メッセージにを返却する。
    return displayMessage;
  }

  onHandleCommonNo13(): void {
    /** 1.PNR情報取得API応答.data.orderEligibilities.firstBaggage.isEligible = false (利用不可)、かつPNR情報取得API応答.data.orderEligibilities.firstBaggage.nonEligibilityReasonsが存在する場合 */
    if (
      !this.mybookingServiceState$.value.pnr?.orderEligibilities?.firstBaggage?.isEligible &&
      !isEmptyArray(this.mybookingServiceState$.value.pnr?.orderEligibilities?.firstBaggage?.nonEligibilityReasons)
    ) {
      /**
       * 以下パラメータで「当画面共通処理No1.利用不可理由に応じたエラーメッセージ表示処理」を実施する
       * 機能: "firstBaggage"
       * ASW不可事由: PNR情報取得API応答.data.orderEligibilities.firstBaggage.nonEligibilityReasons
       * DxAPI不可事由: "-"
       * エラーメッセージID: "E1126"
       */
      this.onHandleCommonNo1(
        FUCNTION_INELIGIBLE_REASON.FIRST_BAGGAGE,
        this.mybookingServiceState$.value.pnr?.orderEligibilities?.firstBaggage?.nonEligibilityReasons,
        '-',
        ERROR_MESSAGE_ID.FIRST_BAGGAGE
      );
    } else {
      /** 2.上記以外の場合、手荷物申込(S02-M016)をモーダル表示する */
      this.openBaggageApplicationModal();
    }
  }

  // (共通No.14) 未申込時ラウンジ申込モーダル表示処理
  onHandleCommonNo14(): void {
    /**
     * (共通No.14.1)
     * PNR情報取得API応答.data.orderEligibilities.chargeableLounge.isEligible = false (利用不可)、かつPNR情報取得API応答.data.orderEligibilities.chargeableLounge.nonEligibilityReasonsが存在する場合
     */
    if (
      !this.mybookingServiceState$.value.pnr?.orderEligibilities?.chargeableLounge?.isEligible &&
      !isEmptyArray(this.mybookingServiceState$.value.pnr?.orderEligibilities?.chargeableLounge?.nonEligibilityReasons)
    ) {
      /**
       * 以下パラメータで「当画面共通処理No1.利用不可理由に応じたエラーメッセージ表示処理」を実施する
       * 機能: "lounge"
       * ASW不可事由: PNR情報取得API応答.data.orderEligibilities.chargebleLouge.nonEligibilityReasons
       * DxAPI不可事由: "-"
       * エラーメッセージID: "E1127"
       */
      this.onHandleCommonNo1(
        FUCNTION_INELIGIBLE_REASON.LOUNGE,
        this.mybookingServiceState$.value.pnr?.orderEligibilities?.chargeableLounge?.nonEligibilityReasons,
        '-',
        ERROR_MESSAGE_ID.LOUNGE
      );
    } else {
      /** (共通No.14.2) 上記以外の場合、ラウンジ申込(S02-M015)をモーダル表示する。*/
      this.openLoungeModal();
    }
  }

  // (共通No.15) 未申込時機内食申込モーダル表示処理
  onHandleCommonNo15(): void {
    // (共通No.15.1) PNR情報取得API応答.data.orderEligibilities.meal.isEligible = false (利用不可)、かつPNR情報取得API応答.data.orderEligibilities.meal.nonEligibilityReasons が存在する場合
    if (
      !this.mybookingServiceState$.value.pnr?.orderEligibilities?.meal?.isEligible &&
      !isEmptyArray(this.mybookingServiceState$.value.pnr?.orderEligibilities?.meal?.nonEligibilityReasons)
    ) {
      /**
       * 以下パラメータで「当画面共通処理No1.利用不可理由に応じたエラーメッセージ表示処理」を実施する
       * 機能: "meal"
       * ASW不可事由: PNR情報取得API応答.data.orderEligibilities.meal.nonEligibilityReasons
       * DxAPI不可事由: "-"
       * エラーメッセージID: "E1128"
       */
      this.onHandleCommonNo1(
        FUCNTION_INELIGIBLE_REASON.MEAL,
        this.mybookingServiceState$.value.pnr?.orderEligibilities?.meal?.nonEligibilityReasons,
        '-',
        ERROR_MESSAGE_ID.MEAL
      );
    } else {
      // (共通No.15.2)	上記以外の場合、機内食申込(S02-M017)をモーダル表示する。
      this.openInFlightMealModal();
    }
  }

  /** (共通No.16) 未申込時マイカーバレー画面遷移処理 */
  async onHandleCommonNo16(): Promise<void> {
    /** 1. 申込操作中のサービス情報が存在するか判定する */
    const isContinueProcess = await this.onHandleProcessCommonWithDialogConfirm();
    if (!isContinueProcess) {
      return;
    }

    /** 2. PNR情報.data.orderEligibilities.myCarValet.isEligible = false (利用不可)、かつPNR情報.data.orderEligibilities.myCarValet.nonEligibilityReasonsが存在する場合 */
    const nonEligibilityReasons = this.mybookingServiceState$.value.pnr?.orderEligibilities?.myCarValet
      ?.nonEligibilityReasons as Array<GetOrderResponseDataOrderEligibilitiesMyCarValet.NonEligibilityReasonsEnum>;
    if (
      !this.mybookingServiceState$.value.pnr?.orderEligibilities?.myCarValet?.isEligible &&
      !isEmptyArray(nonEligibilityReasons)
    ) {
      /**
       * 以下パラメータで「当画面共通処理No1.利用不可理由に応じたエラーメッセージ表示処理」を実施する。
       * 機能: "myCarValet"
       * ASW不可事由: 'PNR情報取得API応答.data.orderEligibilities.myCarValet.nonEligibilityReasons
       * DxAPI不可事由: "-"
       * エラーメッセージID: "E1130"
       */
      this.onHandleCommonNo1(
        FUCNTION_INELIGIBLE_REASON.MY_CAR_VALET,
        nonEligibilityReasons,
        '-',
        ERROR_MESSAGE_ID.MY_CAR_VALET
      );
      return;
    }

    /** 3. ユーザ共通.言語情報 ≠ "ja"(日本語)かつ、ユーザ共通.言語情報 ≠ "en"(英語)の場合 言語選択モーダルを表示する */
    if (
      this._common.aswContextStoreService.aswContextData.lang !== LANGUAGE_JP &&
      this._common.aswContextStoreService.aswContextData.lang !== LANGUAGE_EN
    ) {
      this.openLanguageSelectionModal();
    } else {
      /** それ以外の場合、サービシング共通store.言語コードにユーザ共通.言語情報を保持し、マイカーバレー申込(S02-P011)に遷移する */
      this._servicingCommonStoreService.updateServicingCommon({
        backupLanguage: this._common.aswContextStoreService.aswContextData.lang,
      });
      this._router.navigateByUrl(SERVICING_ROUTES.myCarValetInput);
    }
  }

  // (共通No.17) 未申込時ペットらくのり申込モーダル表示処理
  async onHandleCommonNo17(): Promise<void> {
    /** 1. 申込操作中のサービス情報が存在するか判定する */
    const isContinueProcess = await this.onHandleProcessCommonWithDialogConfirm();
    if (!isContinueProcess) {
      return;
    }

    /**
     * 2. PNR情報取得API応答.data.orderEligibilities.petRakunori.isEligible = false (利用不可)、かつ
     * PNR情報取得API応答.data.orderEligibilities.petRakunori.nonEligibilityReasonsが存在する場合、
     **/
    if (
      !this.mybookingServiceState$.value.pnr?.orderEligibilities?.petRakunori?.isEligible &&
      !isEmptyArray(this.mybookingServiceState$.value.pnr?.orderEligibilities?.petRakunori?.nonEligibilityReasons)
    ) {
      /**
       * 以下パラメータで「当画面共通処理No1.利用不可理由に応じたエラーメッセージ表示処理」を実施する
       * 機能: "pet"
       * ASW不可事由: 'PNR情報取得API応答.data.orderEligibilities.petRakunori.nonEligibilityReasons
       * DxAPI不可事由: "-"
       * エラーメッセージID: "E1132"
       */
      this.onHandleCommonNo1(
        FUCNTION_INELIGIBLE_REASON.PET,
        this.mybookingServiceState$.value.pnr?.orderEligibilities?.petRakunori?.nonEligibilityReasons,
        '-',
        ERROR_MESSAGE_ID.PET
      );
    } else {
      /** 3. 上記以外の場合、ペットらくのり申込(S02-P012)へ遷移する */
      this._router.navigate([FEATURE_TO_ROUTE_MAPPING['S02-P012']]);
    }
  }

  // (共通No.18) 未申込時ジュニアパイロット申込モーダル表示処理
  async onHandleCommonNo18(): Promise<void> {
    /** 1. 申込操作中のサービス情報が存在するか判定する */
    const isContinueProcess = await this.onHandleProcessCommonWithDialogConfirm();
    if (!isContinueProcess) {
      return;
    }

    /**
     * 2. PNR情報取得API応答.data.orderEligibilities.juniorPilot.isEligible = false (利用不可)、かつ
     * PNR情報取得API応答.data.orderEligibilities.juniorPilot.nonEligibilityReasonsが存在する場合
     */
    if (
      !this.mybookingServiceState$.value.pnr?.orderEligibilities?.juniorPilot?.isEligible &&
      !isEmptyArray(this.mybookingServiceState$.value.pnr?.orderEligibilities?.juniorPilot?.nonEligibilityReasons)
    ) {
      /**
       * 以下パラメータで「当画面共通処理No1.利用不可理由に応じたエラーメッセージ表示処理」を実施する
       * 機能: "juniorPilot"
       * ASW不可事由: PNR情報取得API応答.data.orderEligibilities.juniorPilot.nonEligibilityReasons
       * DxAPI不可事由: "-"
       * エラーメッセージID: '"E1133"
       */
      this.onHandleCommonNo1(
        FUCNTION_INELIGIBLE_REASON.JUNIOR_PILOT,
        this.mybookingServiceState$.value.pnr?.orderEligibilities?.juniorPilot?.nonEligibilityReasons,
        '-',
        ERROR_MESSAGE_ID.JUNIOR_PILOT
      );
    } else {
      this._servicingCommonStoreService.updateServicingCommon({
        backupLanguage: this._common.aswContextStoreService.aswContextData.lang,
      });
      /** 3. 上記以外の場合、ジュニアパイロット申込(S02-P013)へ遷移する */
      this._router.navigate([FEATURE_TO_ROUTE_MAPPING['S02-P013']]);
    }
  }

  /** 申込操作中のサービス情報が存在するか判定する */
  async onHandleProcessCommonWithDialogConfirm(): Promise<boolean> {
    /**「当画面共通処理No2. 申込操作中情報有無判定処理」を呼び出し、事前座席指定、ラウンジ、事前追加手荷物、機内食のいずれかが申込操作中を確認する */
    const applicationInProgress = this.onHandleCommonNo2();

    /** 申込操作中有無=true(申込操作中)の場合、パラメータとして"MSG0527"「同時に登録できないため申込操作中の情報が破棄される旨」の確認ダイアログ(G02-005)を表示する */
    if (applicationInProgress) {
      return await firstValueFrom(this.openDialogConfirm('MSG0527')).then((result) => {
        /** 確認ダイアログより「閉じる」ボタン押下が返却された場合、イベント処理共通を終了する */
        if (result.clickType === DialogClickType.CLOSE) {
          return false;
          /** 確認ダイアログより「確認」ボタン押下が返却された場合、「当画面共通処理No3. 全サービスの編集情報破棄処理」を実施した後、以降の処理を継続する */
        } else {
          this.onHandleCommonNo3();
          return true;
        }
      });
    }
    return true;
  }

  /**
   * 共通No. (21) コールセンター手荷物フリーテキスト取得処理
   * @param {string} [ssrCode] SSRコード
   * @returns {string} 画面表示用フリーテキスト
   */
  onHandleCommonNo21(ssrCode?: string): string {
    // 1.	画面表示用フリーテキストに""(空欄)を設定する。
    let freeText = '';
    // 2.	以下の条件でASWDBの「汎用マスターデータ（リスト）」テーブルから情報を取得する。
    const mListData =
      this.myBookingServiceMasterData$.value.listData?.find(
        (item: MListData) =>
          // データコード = "PD_740" AND
          item?.data_code === DATA_CODE.PD_740 &&
          // 言語コード = ユーザ共通.言語情報 AND
          item?.lang === this._common.aswContextStoreService.aswContextData.lang &&
          // value = 入力パラメータ.SSRコード
          item?.value === ssrCode
      ) ?? ({} as MListData);
    // 3.	取得できた場合、画面表示用フリーテキストに汎用マスターデータ（リスト）.表示内容を設定する。
    freeText = mListData.display_content ?? '';
    // 4.	画面表示用フリーテキストを返却する。
    return freeText;
  }

  onHandleAmountService(mongoId: string, prices: any): string {
    const amount = this._amountFormatPipe.transform(prices.price ?? 0, undefined, prices?.currencyCode ?? '');
    if (!isStringEmpty(amount)) {
      return this._staticMsg.transform(mongoId, {
        '0': amount,
      });
    }
    return '';
  }

  destroy(): void {}
}
